import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { listContacts } from '@/api/people'
import { AddPersonInline } from '@/components/owed/AddPersonInline'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { fromPaise, splitEqually, splitError, toPaise, yourShare } from '@/lib/splitMath'

export interface SplitRow {
  contactId: string
  amountPaise: number
  edited: boolean
}
export interface SplitValue {
  rows: SplitRow[]
  includeYou: boolean
}

// eslint-disable-next-line react-refresh/only-export-components -- tests and callers import it alongside the component
export function splitPayload(v: SplitValue): { contactId: string; amount: number }[] {
  return v.rows.map((r) => ({ contactId: r.contactId, amount: fromPaise(r.amountPaise) }))
}

/** Re-divides the bill across the rows the user hasn't typed into, keeping typed rows as-is. */
function rebalance(v: SplitValue, totalPaise: number): SplitValue {
  const fixedPaise = v.rows.filter((r) => r.edited).reduce((s, r) => s + r.amountPaise, 0)
  const freeCount = v.rows.filter((r) => !r.edited).length
  const { sharesPaise } = splitEqually(
    Math.max(totalPaise - fixedPaise, 0),
    freeCount,
    v.includeYou
  )
  let i = 0
  return {
    ...v,
    rows: v.rows.map((r) => (r.edited ? r : { ...r, amountPaise: sharesPaise[i++] ?? 0 })),
  }
}

export function SplitSection({
  totalRupees,
  value,
  onChange,
  knownNames = {},
  excludeYouLabel = "I'm not part of this (I paid for them)",
  defaultOpen = false,
}: {
  totalRupees: number
  value: SplitValue
  onChange: (v: SplitValue) => void
  /** Names for people already on the split, who may since have been archived. */
  knownNames?: Record<string, string>
  excludeYouLabel?: string
  /** Opens the people list straight away (the "Split a bill" entry point). */
  defaultOpen?: boolean
}) {
  const { format } = useFormatCurrency()
  const [open, setOpen] = useState(defaultOpen || value.rows.length > 0)
  const [addedNames, setAddedNames] = useState<Record<string, string>>({})
  const totalPaise = toPaise(totalRupees || 0)
  const { data: contacts = [] } = useQuery({
    queryKey: ['contacts', { includeArchived: false }],
    queryFn: () => listContacts(false),
    enabled: open,
  })

  // Read through refs so only a change in the total re-splits; a new onChange identity must not.
  const latest = useRef({ value, onChange })
  useEffect(() => {
    latest.current = { value, onChange }
  })
  const lastTotal = useRef<number | null>(null)
  useEffect(() => {
    const prev = lastTotal.current
    lastTotal.current = totalPaise
    if (prev === totalPaise) return
    const { value: v, onChange: change } = latest.current
    // On mount only unedited rows (a preselected person) need dividing; loaded rows are exact.
    if (v.rows.length > 0 && (prev !== null || v.rows.some((r) => !r.edited))) {
      const next = rebalance(v, totalPaise)
      // Skip no-ops so merely retyping the bill never marks a loaded split as edited.
      if (next.rows.some((r, i) => r.amountPaise !== v.rows[i].amountPaise)) change(next)
    }
  }, [totalPaise])

  function toggle(contactId: string) {
    const has = value.rows.some((r) => r.contactId === contactId)
    const rows = has
      ? value.rows.filter((r) => r.contactId !== contactId)
      : [...value.rows, { contactId, amountPaise: 0, edited: false }]
    onChange(rebalance({ ...value, rows }, totalPaise))
  }

  // Typing a share leaves the other rows alone: the difference lands on "your share".
  function setShare(contactId: string, raw: string) {
    onChange({
      ...value,
      rows: value.rows.map((r) =>
        r.contactId === contactId
          ? { ...r, amountPaise: toPaise(Number(raw) || 0), edited: true }
          : r
      ),
    })
  }

  // Archived people aren't in the active list but must stay visible (and removable) on an edit.
  const people = [
    ...contacts.map((c) => ({ id: c.id, name: c.name })),
    ...value.rows
      .filter((r) => !contacts.some((c) => c.id === r.contactId))
      .map((r) => ({
        id: r.contactId,
        name: addedNames[r.contactId] ?? knownNames[r.contactId] ?? 'Archived person',
      })),
  ]

  const shares = value.rows.map((r) => r.amountPaise)
  const error = value.rows.length > 0 ? splitError(totalPaise, shares) : null
  const mine = yourShare(totalPaise, shares)

  return (
    <div className="rounded-lg border border-[var(--fl-border)]">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 px-3 py-2 text-sm font-medium text-[var(--fl-text-1)]"
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />} Split with others
      </button>
      {open && (
        <div className="space-y-3 border-t border-[var(--fl-border)] px-3 py-3">
          {people.length === 0 && (
            <p className="text-sm text-[var(--fl-text-2)]">Add someone to split with.</p>
          )}
          {people.map((c) => {
            const row = value.rows.find((r) => r.contactId === c.id)
            return (
              <div key={c.id} className="flex items-center gap-3">
                <Checkbox
                  id={`split-${c.id}`}
                  checked={!!row}
                  onChange={() => toggle(c.id)}
                  aria-label={c.name}
                />
                <label htmlFor={`split-${c.id}`} className="flex-1 text-sm">
                  {c.name}
                </label>
                {row && (
                  <Input
                    aria-label={`${c.name}'s share`}
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    className="w-28"
                    value={fromPaise(row.amountPaise)}
                    onChange={(e) => setShare(c.id, e.target.value)}
                  />
                )}
              </div>
            )
          })}
          <AddPersonInline
            onAdded={(c) => {
              setAddedNames((n) => ({ ...n, [c.id]: c.name }))
              onChange(
                rebalance(
                  {
                    ...value,
                    rows: [...value.rows, { contactId: c.id, amountPaise: 0, edited: false }],
                  },
                  totalPaise
                )
              )
            }}
          />
          <div className="flex items-center gap-2">
            <Checkbox
              id="split-exclude-me"
              checked={!value.includeYou}
              onChange={(e) =>
                onChange(rebalance({ ...value, includeYou: !e.target.checked }, totalPaise))
              }
              aria-label={excludeYouLabel}
            />
            <label htmlFor="split-exclude-me" className="text-sm text-[var(--fl-text-2)]">
              {excludeYouLabel}
            </label>
          </div>
          {value.rows.length > 0 && (
            <p className="text-sm font-medium text-[var(--fl-heading)]">
              Your share {format(fromPaise(mine))}
            </p>
          )}
          {error && <p className="text-xs text-[var(--fl-error)]">{error}</p>}
        </div>
      )}
    </div>
  )
}
